import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { StoreService } from 'src/app/store.service';

export interface MediaFields {
  title?: string;
  caption?: string;
  alt_text?: string;
}

interface UploadPart {
  PartNumber: number;
  ETag: string;
}

/**
 * Uploads media straight to S3 through presigned multipart URLs (the flow Nexus uses),
 * so large files never go through the API's request body, then registers the file in
 * the store's media library.
 */
@Injectable({ providedIn: 'root' })
export class DirectMediaUploadService {
  private readonly MAX_CONCURRENT_PARTS = 4;
  private readonly MAX_RETRIES = 3;

  // No interceptors: the presigned URL is the only auth S3 accepts.
  private readonly s3: HttpClient;

  constructor(private svc: StoreService, backend: HttpBackend) {
    this.s3 = new HttpClient(backend);
  }

  /**
   * Resolves with the media row, same shape as media/create's data (photo is the full URL).
   * onProgress gets 0-100; it stays below 100 until the server has stored the file.
   */
  async upload(file: File, fields: MediaFields = {}, onProgress?: (percent: number) => void): Promise<any> {
    const storeId = localStorage.getItem('store_id');
    const bearer = localStorage.getItem('bearer');

    const endpoint = (await this.svc.getMediaUploadEndpoint(storeId, file, bearer).toPromise())['data'];
    const urls: string[] = endpoint.endpoint_urls;
    const partSize: number = endpoint.part_size || file.size;

    // Set when a part gives up, so the other workers stop instead of sending the rest.
    let failed = false;

    const loaded: number[] = new Array(urls.length).fill(0);
    const report = () => {
      if (failed) {
        return;
      }
      const sent = loaded.reduce((sum, bytes) => sum + bytes, 0);
      onProgress?.(Math.min(99, Math.floor((sent / file.size) * 100)));
    };

    const parts: UploadPart[] = new Array(urls.length);
    let next = 0;
    const worker = async () => {
      while (!failed && next < urls.length) {
        const i = next++;
        const blob = file.slice(i * partSize, Math.min((i + 1) * partSize, file.size));
        try {
          const etag = await this.uploadPart(urls[i], blob, file.type, bytes => { loaded[i] = bytes; report(); });
          parts[i] = { PartNumber: i + 1, ETag: etag };
        } catch (error) {
          failed = true;
          throw error;
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(this.MAX_CONCURRENT_PARTS, urls.length) }, worker));

    const result = await this.svc.completeMediaUpload(
      storeId, endpoint.upload_token, parts, fields.title, fields.caption, fields.alt_text, bearer
    ).toPromise();
    onProgress?.(100);
    return result['data'];
  }

  /** PUTs one part and resolves with its ETag, retrying with backoff. */
  private async uploadPart(url: string, blob: Blob, type: string, onLoaded: (bytes: number) => void, attempt = 0): Promise<string> {
    try {
      return await new Promise<string>((resolve, reject) => {
        this.s3.put(url, blob, {
          headers: new HttpHeaders({ 'Content-Type': type || 'application/octet-stream' }),
          reportProgress: true,
          observe: 'events',
          responseType: 'text',
        }).subscribe(event => {
          if (event.type === HttpEventType.UploadProgress) {
            onLoaded(event.loaded);
          } else if (event.type === HttpEventType.Response) {
            const etag = event.headers.get('ETag');
            etag ? resolve(etag) : reject(new Error('S3 response has no ETag'));
          }
        }, reject);
      });
    } catch (error) {
      if (attempt >= this.MAX_RETRIES) {
        throw error;
      }
      onLoaded(0);
      await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** attempt));
      return this.uploadPart(url, blob, type, onLoaded, attempt + 1);
    }
  }
}

/** The API's reason for a failed upload, when it gave one. */
export function uploadErrorMessage(error: any): string {
  const message = error?.error?.message;
  if (typeof message === 'string') {
    return message;
  }
  if (message && typeof message === 'object') {
    const first = Object.values(message)[0];
    return typeof first === 'string' ? first : '';
  }
  return '';
}
